import clsx from "clsx";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BRANDS } from "@/enums/brands";
import { THEME } from "@/enums/profile";
import { FormatNumber, FormatNumberWithLabel } from "@/utils/common/generic";
import { GetColors } from "@/utils/library/color";
import { Chart } from "@lula-technologies-inc/wingman/dist/commonjs/models/components";
import { Bar } from "../common/charts/Bar";

const FAKE_GROUP = "$$$";

const AskBarChart: React.FC<{
  chartData: Chart;
  themeOverride?: THEME;
  brandOverride?: BRANDS;
}> = ({ chartData, themeOverride }) => {
  const { t } = useTranslation("ask");
  const { t: ct } = useTranslation("common");

  const [title, setTitle] = React.useState<string | undefined>(chartData.title);
  const [data, setData] = React.useState<any[]>([]);
  const [groups, setGroups] = React.useState<string[]>([]);

  useEffect(() => {
    if (chartData.title !== title) {
      setTitle(chartData.title);
    }

    const chartGroups = Array.from(
      new Set(chartData.data.map((data) => data.group || FAKE_GROUP))
    );

    const colors = GetColors(chartGroups.length > 0 ? chartGroups.length : 1);
    let barData: any[] = chartData.data.map((data) => {
      const groupColor = colors[chartGroups.indexOf(data.group ?? FAKE_GROUP)];

      return {
        label: data.label,
        [data.group ?? FAKE_GROUP]: data.value,
        [(data.group ?? FAKE_GROUP) + "Color"]: groupColor,
      };
    });

    setGroups(chartGroups);

    barData = barData.reduce((acc, obj) => {
      const existing = acc.find(
        (item: { label: any }) => item.label === obj.label
      );

      if (existing) {
        Object.keys(obj).forEach((key) => {
          if (key !== "label") {
            existing[key] = obj[key];
          }
        });
      } else {
        acc.push(obj);
      }

      return acc;
    }, []);

    setData(barData);
  }, [chartData]);

  if (!data || data.length === 0) {
    return <div>{t("chartFailed")}</div>;
  }

  return (
    <div className={clsx("h-96", "mb-8")}>
      {title && (
        <h1 className={clsx("text-center", "text-2xl", "font-bold", "mb-4")}>
          {title}
        </h1>
      )}
      <Bar
        data={data}
        themeOverride={themeOverride}
        keys={groups}
        indexBy="label"
        groupMode="grouped"
        label={(d) => (d.value > 0 ? FormatNumber(d.value, ct, 0) : "")}
        tooltip={(d) => (
          <div
            className={clsx(
              "p-2",
              "bg-panel",
              "shadow-lg",
              "rounded-lg",
              "border"
            )}
          >
            {FormatNumberWithLabel(d.id, d.value, ct)}
          </div>
        )}
      />
    </div>
  );
};

export default AskBarChart;
